/**
 * AdminInterpretersBulkPage — Editor masivo estilo hoja de cálculo.
 *
 * Permite editar TODOS los intérpretes en una tabla, marcar las filas modificadas
 * y guardar todos los cambios en una sola request. La biografía vive detrás de un
 * botón "Editar biografía" que abre un panel expandible bajo la fila — así la
 * tabla no se satura visualmente.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, Search, FileText, X,
  Edit3, AlertCircle, CheckCircle2, RotateCcw, Eye, EyeOff, Upload,
  Keyboard, Download,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { fuzzyMatch } from '../../lib/textSearch';
import { toast } from 'sonner';
import CsvImportDialog from '../../components/CsvImportDialog';
import ChipMultiSelect from '../../components/ChipMultiSelect';
import ColumnFilter from '../../components/ColumnFilter';

// Columns in display order. `kind` controls the cell editor type.
// Widths are tuned to fit in ~1400px so most laptops show all columns
// without horizontal scroll (the wrapper still scrolls on narrower screens).
const COLUMNS = [
  { key: 'full_name',              label: 'Nombre completo',  kind: 'text',  width: 'w-[180px]' },
  { key: 'email',                  label: 'Email principal',  kind: 'text',  width: 'w-[180px]' },
  { key: 'additional_emails',      label: 'Correos +',        kind: 'chips', width: 'w-[160px]' },
  { key: 'phone',                  label: 'Tel. principal',   kind: 'text',  width: 'w-[120px]' },
  { key: 'additional_phones',      label: 'Teléfonos +',      kind: 'chips', width: 'w-[150px]' },
  { key: 'city',                   label: 'Ciudad',           kind: 'text',  width: 'w-[140px]' },
  { key: 'active_languages',       label: 'Idiomas activos',  kind: 'chips', width: 'w-[170px]', catalog: 'languages' },
  { key: 'passive_languages',      label: 'Idiomas pasivos',  kind: 'chips', width: 'w-[170px]', catalog: 'languages' },
  { key: 'specialty',              label: 'Especialidad',     kind: 'chips', width: 'w-[170px]', catalog: 'specialties' },
  { key: 'modalities',             label: 'Modalidades',      kind: 'chips', width: 'w-[180px]', catalog: 'modalities' },
  { key: 'linkedin_url',           label: 'LinkedIn',         kind: 'text',  width: 'w-[170px]' },
  { key: 'website',                label: 'Página web',       kind: 'text',  width: 'w-[170px]' },
  { key: 'is_active',              label: 'Activo',           kind: 'bool',  width: 'w-[72px]' },
  { key: 'is_listed_in_directory', label: 'Visible',          kind: 'bool',  width: 'w-[80px]' },
  { key: 'data_verified',          label: 'Verificado',       kind: 'bool',  width: 'w-[90px]' },
];

function normalizeList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  return String(v).split(/[|,]/).map((x) => x.trim()).filter(Boolean);
}

function listToText(v) {
  return Array.isArray(v) ? v.join(' | ') : (v || '');
}

function shallowEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((x, i) => String(x) === String(b[i]));
  }
  return false;
}

// Display values used by the Excel-style column filters (must match ColumnFilter).
function colDisplayValues(row, col) {
  const raw = row[col.key];
  if (col.kind === 'chips') { const a = normalizeList(raw); return a.length ? a : ['(Vacías)']; }
  if (col.kind === 'bool') return [raw ? 'Sí' : 'No'];
  const s = (raw ?? '').toString().trim();
  return [s || '(Vacías)'];
}
function colSortKey(row, col) {
  const raw = row[col.key];
  if (col.kind === 'chips') return normalizeList(raw).join(', ').toLowerCase();
  if (col.kind === 'bool') return raw ? '1' : '0';
  return (raw ?? '').toString().toLowerCase();
}

export default function AdminInterpretersBulkPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);             // server snapshot
  const [edits, setEdits] = useState({});           // slug → { field: newValue }
  const [bioOpen, setBioOpen] = useState(null);     // slug of the row whose bio panel is open
  const [search, setSearch] = useState('');
  const [showOnlyDirty, setShowOnlyDirty] = useState(false);
  const [hideVerified, setHideVerified] = useState(false);
  // Excel-style column filters/sort.
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const [columnFilters, setColumnFilters] = useState({}); // key -> Set<string>
  // By default only show active + listed-in-directory interpreters (the ones the
  // user actually cares about cleaning up). A toggle lets advanced admins see all.
  const [showAll, setShowAll] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastResults, setLastResults] = useState(null);
  // Global catalogs for the chip-multi-select cells (languages, specialties).
  // Built on first GET from existing interpreter data; admins can extend live.
  const [catalogs, setCatalogs] = useState({ languages: [], specialties: [], modalities: [] });

  async function loadCatalog(name) {
    try {
      const { data } = await api.get(`/admin/catalogs/${name}`);
      setCatalogs((c) => ({ ...c, [name]: data.values || [] }));
    } catch (e) { /* non-fatal */ }
  }
  async function addToCatalog(name, value) {
    const { data } = await api.post(`/admin/catalogs/${name}`, { value });
    setCatalogs((c) => ({ ...c, [name]: data.values || [] }));
    return data.values;
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/interpreters');
      setRows(data || []);
      setEdits({});
      setLastResults(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); loadCatalog('languages'); loadCatalog('specialties'); loadCatalog('modalities'); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const dirtyCount = Object.keys(edits).filter((slug) => Object.keys(edits[slug] || {}).length > 0).length;

  // Get the *effective* value for a cell (edit overlay > original)
  function getCellValue(row, key) {
    const e = edits[row.slug];
    if (e && key in e) return e[key];
    return row[key];
  }

  function setCellValue(slug, key, value) {
    const original = rows.find((r) => r.slug === slug);
    if (!original) return;
    setEdits((prev) => {
      const cur = { ...(prev[slug] || {}) };
      // If the new value matches the original, remove the override (un-dirty).
      const orig = original[key];
      if (Array.isArray(orig) || Array.isArray(value)) {
        if (shallowEqual(value, orig)) {
          delete cur[key];
        } else {
          cur[key] = value;
        }
      } else if ((value ?? '') === (orig ?? '')) {
        delete cur[key];
      } else {
        cur[key] = value;
      }
      const next = { ...prev };
      if (Object.keys(cur).length === 0) {
        delete next[slug];
      } else {
        next[slug] = cur;
      }
      return next;
    });
  }

  function revertRow(slug) {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  }

  function revertAll() {
    if (!dirtyCount) return;
    if (!window.confirm(`¿Descartar TODOS los cambios (${dirtyCount} fila${dirtyCount === 1 ? '' : 's'})?`)) return;
    setEdits({});
  }

  async function saveAll() {
    const changes = Object.entries(edits)
      .map(([slug, ch]) => ({ slug, changes: ch }))
      .filter((e) => Object.keys(e.changes || {}).length > 0);
    if (!changes.length) return;
    setSaving(true);
    try {
      const { data } = await api.post('/admin/interpreters/bulk-update', { changes });
      setLastResults(data);
      if (data.failed === 0) {
        toast.success(`Guardado: ${data.ok} fila${data.ok === 1 ? '' : 's'} actualizada${data.ok === 1 ? '' : 's'}`);
      } else {
        toast.warning(`${data.ok} guardadas, ${data.failed} con errores. Revisa abajo.`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  // Keyboard shortcuts for power users editing many rows at once.
  //   Ctrl/Cmd+S   → Save all pending changes
  //   Ctrl/Cmd+Z   → Discard all pending changes (with confirm)
  // Tab/Enter/Esc are handled by the native button/input focus behavior:
  //   • Tab moves between cells (text inputs + chip triggers + bool buttons)
  //   • Enter activates the focused button (opens chip popover / toggles bool)
  //   • Esc closes the chip popover (handled in ChipMultiSelect)
  useEffect(() => {
    function onKey(e) {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const k = e.key.toLowerCase();
      if (k === 's') {
        // Only intercept when there ARE changes; otherwise let the browser do its thing.
        if (dirtyCount > 0 && !saving) {
          e.preventDefault();
          saveAll();
        }
      } else if (k === 'z' && e.shiftKey) {
        // Cmd+Shift+Z → discard. Reserved Cmd+Z for native undo inside inputs.
        if (dirtyCount > 0 && !saving) {
          e.preventDefault();
          revertAll();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtyCount, saving, edits]);

  // Bio panel — guarda solo este campo (no toca el resto).
  async function saveBio(slug, newBio, newReferences) {
    setSaving(true);
    try {
      const ch = {};
      const row = rows.find((r) => r.slug === slug);
      if ((newBio ?? '') !== (row?.bio ?? '')) ch.bio = newBio;
      if ((newReferences ?? '') !== (row?.references_text ?? '')) ch.references_text = newReferences;
      if (!Object.keys(ch).length) {
        setBioOpen(null);
        return;
      }
      await api.post('/admin/interpreters/bulk-update', { changes: [{ slug, changes: ch }] });
      toast.success('Biografía actualizada');
      setBioOpen(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get('/admin/interpreters/export', { responseType: 'blob' });
      // Pull the filename from Content-Disposition when available.
      let fname = `cmic-interpretes-${new Date().toISOString().slice(0, 10)}.csv`;
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m) fname = m[1];
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV descargado. Edítalo en Excel y vuelve a importarlo.');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo exportar el CSV');
    } finally {
      setExporting(false);
    }
  }

  const filtered = useMemo(() => {
    let out = rows.filter((r) => {
      // Default: only active AND listed-in-directory interpreters. Toggle off to
      // see everyone (including inactive / hidden ones).
      if (!showAll) {
        if (r.is_active === false) return false;
        if (r.is_listed_in_directory === false) return false;
      }
      if (hideVerified && r.data_verified === true) return false;
      if (showOnlyDirty && !edits[r.slug]) return false;
      if (search.trim().length >= 2) {
        const hay = `${r.full_name || ''} ${r.email || ''} ${(r.additional_emails || []).join(' ')} ${(r.additional_phones || []).join(' ')} ${r.city || ''} ${(r.active_languages || []).join(' ')} ${(r.passive_languages || []).join(' ')} ${Array.isArray(r.specialty) ? r.specialty.join(' ') : (r.specialty || '')} ${(r.modalities || []).join(' ')} ${r.linkedin_url || ''} ${r.website || ''}`;
        if (!fuzzyMatch(search, hay)) return false;
      }
      // Per-column (Excel-style) value filters.
      for (const key of Object.keys(columnFilters)) {
        const sel = columnFilters[key];
        if (!sel || sel.size === 0) continue;
        const col = COLUMNS.find((c) => c.key === key);
        if (!col) continue;
        const vals = colDisplayValues(r, col);
        if (!vals.some((v) => sel.has(v))) return false;
      }
      return true;
    });
    if (sortConfig.key) {
      const col = COLUMNS.find((c) => c.key === sortConfig.key);
      if (col) {
        out = [...out].sort((a, b) => {
          const cmp = colSortKey(a, col).localeCompare(colSortKey(b, col), 'es', { numeric: true });
          return sortConfig.dir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return out;
  }, [rows, edits, search, showOnlyDirty, showAll, hideVerified, columnFilters, sortConfig]);

  const activeFilterCount = Object.keys(columnFilters).length + (sortConfig.key ? 1 : 0);

  // Counts for the toolbar hint
  const activeVisibleCount = useMemo(
    () => rows.filter((r) => r.is_active !== false && r.is_listed_in_directory !== false).length,
    [rows],
  );

  return (
    <div data-testid="admin-interpreters-bulk-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <Link to="/admin/interpreters" className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-cmic-600 mb-2" data-testid="bulk-back-link">
            <ArrowLeft size={14} /> Volver a Intérpretes
          </Link>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <Edit3 size={22} className="text-cmic-500" /> Editor masivo de intérpretes
          </h1>
          <p className="text-sm text-charcoal-500 mt-1">
            Edita varias filas a la vez. Las celdas modificadas se resaltan en amarillo. Guarda todo de una vez con un solo botón.
          </p>
          <KeyboardShortcutsHint />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={exportCsv}
          disabled={exporting}
          data-testid="bulk-export-csv-btn"
          className="inline-flex items-center gap-2 bg-white border border-cmic-300 text-cmic-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-cmic-50 disabled:opacity-50"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Exportar CSV/Excel
        </button>
        <button
          onClick={() => setCsvOpen(true)}
          data-testid="bulk-import-csv-btn"
          className="inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-emerald-50"
        >
          <Upload size={14} /> Importar desde CSV
        </button>
        </div>
      </div>

      {/* Toolbar sticky con guardar */}
      <div className="sticky top-0 z-20 bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap items-center gap-3 mb-3 shadow-sm">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, ciudad, idioma…"
            data-testid="bulk-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none"
          />
        </div>

        <button
          onClick={() => setShowAll((v) => !v)}
          data-testid="bulk-show-all-toggle"
          title={showAll ? `Mostrando los ${rows.length} intérpretes (incluye inactivos y ocultos)` : `Mostrando solo los ${activeVisibleCount} activos y visibles en el directorio`}
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border ${
            showAll
              ? 'bg-charcoal-100 border-charcoal-300 text-charcoal-700'
              : 'bg-emerald-50 border-emerald-300 text-emerald-700'
          }`}
        >
          {showAll ? <EyeOff size={12} /> : <Eye size={12} />}
          {showAll ? `Todos (${rows.length})` : `Solo activos (${activeVisibleCount})`}
        </button>

        <button
          onClick={() => setShowOnlyDirty((v) => !v)}
          data-testid="bulk-only-dirty-toggle"
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border ${
            showOnlyDirty
              ? 'bg-amber-100 border-amber-300 text-amber-800'
              : 'bg-white border-gray-200 text-charcoal-600 hover:border-gray-300'
          }`}
        >
          {showOnlyDirty ? <EyeOff size={12} /> : <Eye size={12} />}
          {showOnlyDirty ? 'Mostrando solo cambios' : 'Mostrar solo modificados'}
        </button>

        <button
          onClick={() => setHideVerified((v) => !v)}
          data-testid="bulk-hide-verified-toggle"
          title="Oculta los intérpretes ya verificados para enfocarte en los que faltan."
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border ${
            hideVerified
              ? 'bg-cmic-100 border-cmic-300 text-cmic-700'
              : 'bg-white border-gray-200 text-charcoal-600 hover:border-gray-300'
          }`}
        >
          {hideVerified ? <EyeOff size={12} /> : <CheckCircle2 size={12} />}
          {hideVerified ? 'Ocultando verificados' : 'Ocultar verificados'}
        </button>

        {activeFilterCount > 0 && (
          <button
            onClick={() => { setColumnFilters({}); setSortConfig({ key: null, dir: 'asc' }); }}
            data-testid="bulk-clear-filters-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
          >
            <X size={12} /> Limpiar filtros ({activeFilterCount})
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-charcoal-500" data-testid="bulk-dirty-count">
            {dirtyCount > 0 ? `${dirtyCount} fila${dirtyCount === 1 ? '' : 's'} con cambios sin guardar` : 'Sin cambios pendientes'}
          </span>
          <button
            onClick={revertAll}
            disabled={dirtyCount === 0 || saving}
            data-testid="bulk-revert-all-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-gray-200 text-charcoal-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RotateCcw size={12} /> Descartar
          </button>
          <button
            onClick={saveAll}
            disabled={dirtyCount === 0 || saving}
            data-testid="bulk-save-all-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cmic-500 to-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar todo
          </button>
        </div>
      </div>

      {/* Banner con resultados del último guardado */}
      {lastResults && lastResults.failed > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-amber-800 mb-1">
            <AlertCircle size={14} /> {lastResults.failed} fila(s) con errores
          </div>
          <ul className="text-xs text-amber-700 list-disc ml-6">
            {lastResults.results.filter((r) => !r.ok).map((r) => (
              <li key={r.slug}>{r.slug}: {r.error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-16 text-charcoal-400">
          <Loader2 size={28} className="animate-spin mx-auto mb-2" />
          Cargando intérpretes…
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="overflow-x-auto rounded-xl">
            <table className="text-sm border-collapse w-full" style={{ minWidth: '2040px' }} data-testid="bulk-table">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-charcoal-500 border-b border-gray-200 w-[32px]">#</th>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={`text-left px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-charcoal-500 border-b border-gray-200 ${c.width}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate">{c.label}</span>
                        <ColumnFilter
                          col={c}
                          rows={rows}
                          sortConfig={sortConfig}
                          setSortConfig={setSortConfig}
                          columnFilters={columnFilters}
                          setColumnFilters={setColumnFilters}
                          normalizeList={normalizeList}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="text-left px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-charcoal-500 border-b border-gray-200 w-[96px]">Bio</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={COLUMNS.length + 2} className="text-center py-8 text-charcoal-400">No hay resultados.</td></tr>
                )}
                {filtered.map((row, idx) => {
                  const isDirty = !!edits[row.slug];
                  const isBioOpen = bioOpen === row.slug;
                  return (
                    <React.Fragment key={row.slug}>
                      <tr className={`${isDirty ? 'bg-amber-50/40' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} hover:bg-cmic-50/30 transition-colors`} data-testid={`bulk-row-${row.slug}`}>
                        <td className="px-2 py-2 text-xs text-charcoal-400 border-b border-gray-100 align-top">{idx + 1}</td>
                        {COLUMNS.map((c) => {
                          const val = getCellValue(row, c.key);
                          const original = row[c.key];
                          const cellDirty = Array.isArray(original) || Array.isArray(val)
                            ? !shallowEqual(val, original)
                            : (val ?? '') !== (original ?? '');
                          return (
                            <td
                              key={c.key}
                              className={`px-1.5 py-1 border-b border-gray-100 align-top ${cellDirty ? 'bg-amber-100/60' : ''}`}
                            >
                              <CellEditor
                                kind={c.kind}
                                value={val}
                                onChange={(v) => setCellValue(row.slug, c.key, v)}
                                testid={`cell-${c.key}-${row.slug}`}
                                catalog={c.catalog ? (catalogs[c.catalog] || []) : null}
                                onCatalogAdd={c.catalog ? (v) => addToCatalog(c.catalog, v) : null}
                              />
                            </td>
                          );
                        })}
                        <td className="px-1.5 py-1 border-b border-gray-100 align-top">
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={() => setBioOpen(isBioOpen ? null : row.slug)}
                              data-testid={`bio-toggle-${row.slug}`}
                              title={row.bio ? 'Editar biografía' : 'Agregar biografía'}
                              className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-1 rounded ${
                                isBioOpen ? 'bg-cmic-500 text-white' : 'bg-cmic-50 text-cmic-700 hover:bg-cmic-100'
                              }`}
                            >
                              <FileText size={10} />
                              {row.bio ? 'Editar' : 'Agregar'}
                            </button>
                            {isDirty && (
                              <button
                                onClick={() => revertRow(row.slug)}
                                data-testid={`bulk-revert-row-${row.slug}`}
                                title="Descartar cambios de esta fila"
                                className="p-1 rounded text-charcoal-400 hover:text-rose-600 hover:bg-rose-50"
                              >
                                <X size={11} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isBioOpen && (
                        <BioPanel row={row} onCancel={() => setBioOpen(null)} onSave={(b, r) => saveBio(row.slug, b, r)} saving={saving} />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CsvImportDialog
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        rows={rows}
        onApplied={() => load()}
      />
    </div>
  );
}

function CellEditor({ kind, value, onChange, testid, catalog, onCatalogAdd }) {
  if (kind === 'bool') {
    return (
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded ${
          value ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-charcoal-500'
        }`}
      >
        {value ? <CheckCircle2 size={11} /> : <X size={11} />}
        {value ? 'Sí' : 'No'}
      </button>
    );
  }
  if (kind === 'chips') {
    // normalize incoming value: server may return list OR " | "-joined string.
    const arr = normalizeList(value);
    return (
      <ChipMultiSelect
        value={arr}
        onChange={onChange}
        catalog={catalog || []}
        onCatalogAdd={onCatalogAdd || (async () => {})}
        placeholder="Vacío — clic para agregar"
        testid={testid}
        compactLimit={2}
      />
    );
  }
  if (kind === 'list') {
    // Legacy fallback (no longer used in COLUMNS but kept for safety).
    return (
      <input
        type="text"
        value={listToText(value)}
        onChange={(e) => onChange(normalizeList(e.target.value))}
        data-testid={testid}
        placeholder="Separa con |"
        className="w-full text-xs border border-transparent bg-transparent rounded px-1.5 py-1 focus:bg-white focus:border-cmic-300 focus:ring-1 focus:ring-cmic-200 outline-none"
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full text-xs border border-transparent bg-transparent rounded px-1.5 py-1 focus:bg-white focus:border-cmic-300 focus:ring-1 focus:ring-cmic-200 outline-none"
    />
  );
}

function BioPanel({ row, onCancel, onSave, saving }) {
  const [bio, setBio] = useState(row.bio || '');
  const [refs, setRefs] = useState(row.references_text || '');
  return (
    <tr className="bg-cmic-50/50" data-testid={`bio-panel-${row.slug}`}>
      <td colSpan={COLUMNS.length + 2} className="p-4 border-b border-gray-200">
        <div className="bg-white rounded-lg border border-cmic-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-charcoal-800 flex items-center gap-2">
              <FileText size={14} className="text-cmic-600" />
              Biografía y referencias — {row.full_name}
            </h4>
            <button onClick={onCancel} className="text-charcoal-400 hover:text-charcoal-700 p-1" data-testid={`bio-close-${row.slug}`}>
              <X size={16} />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-charcoal-600 uppercase tracking-wider mb-1">Biografía</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={5}
                data-testid={`bio-textarea-${row.slug}`}
                placeholder="Biografía pública del intérprete…"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-charcoal-600 uppercase tracking-wider mb-1">Referencias / Trayectoria</label>
              <textarea
                value={refs}
                onChange={(e) => setRefs(e.target.value)}
                rows={3}
                data-testid={`refs-textarea-${row.slug}`}
                placeholder="Conferencias, clientes, certificaciones…"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 outline-none"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={onCancel} className="px-3 py-1.5 text-sm font-medium text-charcoal-600 hover:bg-gray-100 rounded-md">
                Cancelar
              </button>
              <button
                onClick={() => onSave(bio, refs)}
                disabled={saving}
                data-testid={`bio-save-${row.slug}`}
                className="inline-flex items-center gap-1.5 bg-cmic-500 text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-cmic-600 disabled:opacity-60"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Guardar biografía
              </button>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

function KeyboardShortcutsHint() {
  const [open, setOpen] = useState(false);
  // Detect platform for the right modifier label (⌘ on Mac, Ctrl elsewhere).
  const isMac = typeof navigator !== 'undefined' && /Mac|iPad|iPhone/.test(navigator.platform);
  const mod = isMac ? '⌘' : 'Ctrl';
  const shortcuts = [
    { keys: ['Tab'],                   desc: 'Saltar a la siguiente celda' },
    { keys: ['Shift', 'Tab'],          desc: 'Saltar a la celda anterior' },
    { keys: ['Enter'],                 desc: 'Abrir popover / alternar Sí/No' },
    { keys: ['Esc'],                   desc: 'Cerrar popover' },
    { keys: [mod, 'S'],                desc: 'Guardar todos los cambios' },
    { keys: [mod, 'Shift', 'Z'],       desc: 'Descartar todos los cambios' },
  ];
  return (
    <div className="relative inline-block mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="bulk-shortcuts-hint-btn"
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-charcoal-500 hover:text-cmic-700 bg-gray-50 hover:bg-cmic-50 border border-gray-200 rounded-full px-2.5 py-1"
      >
        <Keyboard size={12} /> Atajos de teclado
      </button>
      {open && (
        <div
          data-testid="bulk-shortcuts-hint-panel"
          className="absolute z-30 mt-2 left-0 min-w-[280px] bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-xs"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-charcoal-800 flex items-center gap-1.5">
              <Keyboard size={13} className="text-cmic-600" /> Atajos
            </span>
            <button type="button" onClick={() => setOpen(false)} className="text-charcoal-400 hover:text-charcoal-600">
              <X size={12} />
            </button>
          </div>
          <ul className="space-y-1.5">
            {shortcuts.map((s) => (
              <li key={s.desc} className="flex items-center justify-between gap-3">
                <span className="text-charcoal-600">{s.desc}</span>
                <span className="flex items-center gap-1">
                  {s.keys.map((k, i) => (
                    <kbd
                      key={i}
                      className="font-mono text-[10px] bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 text-charcoal-700 min-w-[18px] text-center"
                    >
                      {k}
                    </kbd>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

